'use client'

import { useQueries } from '@tanstack/react-query'
import axios from 'axios'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { LinkContext } from '../../../contexts'
import { cn } from '../../../utils/cn'

import { AAnnualProgramPreviewModules } from './_components/a-annual-program-preview-modules'
import { AAnnualProgramPreviewProgram } from './_components/a-annual-program-preview-program'
import { ORDER_TERMS_BASE } from './_constants'
import { usePreviewVisibility } from './_hooks/use-preview-visibility'
import { useProgramSwitcher } from './_hooks/use-program-switcher'
import { convertAnnualProgramToProgram } from './_utils/convert-annual-to-program'

import type { IMAnnualProgramPreviewProps, IProgramSection } from './_types/types'
import type { IAnnualProgram } from '../../../types/annual-programs'
import type { IProgramModule } from '../../../types/module'

/**
 * MAnnualProgramPreview Block Component
 *
 * Fetches annual programs from GET /api/v1/web/yearProgram/yearPrograms/{id} for each entry in annualProgramsConfig
 * (in parallel via Promise.all). When multiple programs are loaded, shows switcher buttons (top left of the card)
 * to switch between them; otherwise shows a single program card. Renders nothing when no program is available
 * (loading, error, or empty).
 *
 * @example
 * ```tsx
 * <MAnnualProgramPreview apiBaseUrl="https://api.dev.medante.sk" annualProgramsConfig={[{ programId: 22, name: 'Deti', color: 'sand', modulesType: 'dynamicModules' }]} />
 * ```
 */

function unwrapProgram(body: IAnnualProgram | { data: IAnnualProgram }): IAnnualProgram | null {
	if (!body || typeof body !== 'object') return null
	return 'data' in body ? (body as { data: IAnnualProgram }).data : (body as IAnnualProgram)
}

function formatPrice(value: number): string {
	return `${value.toFixed(2).replace('.', ',')} €`
}

function formatOrderLabel(
	template: string,
	price: number | undefined,
	oldPrice: number | undefined
): string {
	const priceStr = price != null ? formatPrice(price) : '—'
	const oldPriceStr = oldPrice != null ? formatPrice(oldPrice) : '—'
	return template
		.replace(/\{price\}/g, priceStr)
		.replace(/\{oldPrice\}/g, oldPriceStr)
		.replace(/\{priceOld\}/g, oldPriceStr)
}

const DefaultLink = 'a'

const DEFAULT_PROGRAM_SECTION: Required<IProgramSection> = {
	headerOrderButtonLabel: 'Objednať ročný program',
	orderButtonLabel: 'Objednať program ({price})',
	showAddModulesButton: false,
	showProgramsSwitch: true
}

export const MAnnualProgramPreview: React.FC<IMAnnualProgramPreviewProps> = ({
	id,
	apiBaseUrl,
	annualProgramsConfig,
	className,
	style,
	programSection: programSectionProp,
	modulesSection,
	link: linkProp
}) => {
	const linkFromContext = useContext(LinkContext)
	const link = linkProp ?? linkFromContext ?? DefaultLink

	const programSectionResolved = useMemo(
		() => ({ ...DEFAULT_PROGRAM_SECTION, ...programSectionProp }),
		[programSectionProp]
	)

	const [selectedIndex, setSelectedIndex] = useState(0)
	const [selectedModules, setSelectedModules] = useState<IProgramModule[]>([])

	const sectionRef = useRef<HTMLElement>(null)

	const programIds = useMemo(
		() => annualProgramsConfig?.map(p => p.programId) ?? [],
		[annualProgramsConfig]
	)
	const baseUrl = apiBaseUrl?.replace(/\/$/, '') ?? ''
	const enabled = Boolean(baseUrl && programIds.length > 0)

	const queryResults = useQueries({
		queries: programIds.map(programId => ({
			queryKey: ['annualProgram', baseUrl, programId] as const,
			queryFn: async (): Promise<IAnnualProgram | null> => {
				const { data } = await axios.get<IAnnualProgram | { data: IAnnualProgram }>(
					`${baseUrl}/v1/web/yearProgram/yearPrograms/${programId}`
				)
				return unwrapProgram(data)
			},
			enabled
		}))
	})

	const programs = useMemo(
		() => queryResults.map(r => r.data).filter((p): p is IAnnualProgram => p != null),
		[queryResults]
	)
	const loading = queryResults.some(r => r.isPending)
	const error = queryResults.find(r => r.isError)?.error ?? null

	const sectionMounted = !loading && !error && !!programs.length
	const isFooterVisible = usePreviewVisibility(sectionRef, sectionMounted)

	// Handle program switching and scrolling via data attributes
	useProgramSwitcher({ programs, onProgramChange: setSelectedIndex })

	const effectiveIndex = Math.min(selectedIndex, Math.max(0, programs.length - 1))

	// Reset selected modules when program changes
	useEffect(() => {
		setSelectedModules([])
	}, [effectiveIndex])

	const currentProgram = useMemo(() => {
		const program = programs[effectiveIndex] ?? programs[0]
		const currentProgramConfig =
			annualProgramsConfig?.[effectiveIndex] ?? annualProgramsConfig?.[0]
		if (loading || error || !program) return null
		const convertedProgram = convertAnnualProgramToProgram(program)
		// Use programId from config so selection/order URL match the configured program (e.g. 22), not API response id
		if (currentProgramConfig?.programId != null) {
			convertedProgram.id = currentProgramConfig.programId
		}
		// When programItemsType is staticItems, use programItems from config instead of API benefits
		const useStaticItems =
			currentProgramConfig?.programItemsType === 'staticItems' &&
			currentProgramConfig?.programItems &&
			currentProgramConfig.programItems.length > 0
		if (useStaticItems) {
			convertedProgram.items = currentProgramConfig.programItems!
		}
		return convertedProgram
	}, [programs, effectiveIndex, loading, error, annualProgramsConfig])

	const currentAnnualProgram = programs[effectiveIndex]
	const currentProgramConfig = annualProgramsConfig?.[effectiveIndex] ?? annualProgramsConfig?.[0]

	// Generate order URL with selected modules (using ORDER_TERMS_BASE)
	const orderUrl = useMemo(() => {
		if (!currentProgram) return undefined
		const moduleIds = selectedModules.map(m => m.id).join(',')
		const url = `${ORDER_TERMS_BASE}?type=annual-program&id=${currentProgram.id}`
		return moduleIds ? `${url}&extraModules=${moduleIds}` : url
	}, [currentProgram, selectedModules])

	// Update currentProgram with new orderUrl
	const programWithOrderUrl = useMemo(() => {
		if (!currentProgram) return null
		return { ...currentProgram, orderUrl }
	}, [currentProgram, orderUrl])

	const programModules = (currentAnnualProgram?.modules as IProgramModule[]) ?? []
	const hasModules = programModules.length > 0
	const currentModulesType = currentProgramConfig?.modulesType
	const currentModulesText = currentProgramConfig?.modulesText
	// Show section when modulesSection exists and current program has modulesType 'dynamicModules' or 'text' (with modulesText), or when modulesType is unset (backward compat: show if has modules or modulesText)
	const showModulesSection = Boolean(
		modulesSection &&
		(currentModulesType === 'dynamicModules' ||
			(currentModulesType === 'text' && currentModulesText) ||
			(currentModulesType == null && (hasModules || currentModulesText)))
	)

	// Format order button label for sticky footer (desktop/mobile versions). Supports {price} and {oldPrice}/{priceOld} in template.
	const orderButtonLabelFormatted = useMemo(() => {
		const template = programSectionResolved.orderButtonLabel
		const customLabel =
			template != null
				? formatOrderLabel(template, currentProgram?.price, currentProgram?.oldPrice)
				: undefined

		const defaultLabel =
			currentProgram?.price != null
				? currentProgram?.oldPrice != null
					? `Objednať (${formatPrice(currentProgram.price)} <span class="m-program-card__footer-old-price" style="text-decoration: line-through">${formatPrice(currentProgram.oldPrice)}</span>)`
					: `Objednať program (${formatPrice(currentProgram.price)})`
				: 'Objednať program'

		return {
			desktop: customLabel && customLabel.length > 0 ? customLabel : defaultLabel,
			mobile: defaultLabel
		}
	}, [programSectionResolved.orderButtonLabel, currentProgram?.price, currentProgram?.oldPrice])

	// Modules section theme class from selected program's color (used for SCSS modifier --blue, --cyclamen, etc.)
	const getThemeClass = () => {
		const theme = currentProgramConfig?.color ?? currentProgram?.color
		if (!theme) return ''
		return typeof theme === 'string' ? theme.toLowerCase() : ''
	}

	// Render program switcher (used in both sections)
	const renderSwitcher = () => {
		if (!annualProgramsConfig || annualProgramsConfig.length <= 1) return null
		return (
			<div className='m-annual-program-preview__switcher'>
				{annualProgramsConfig.map((p, i) => {
					const colorClass = typeof p.color === 'string' ? p.color.toLowerCase() : ''
					const isActive = i === effectiveIndex
					return (
						<button
							key={p.programId}
							type='button'
							className={cn('m-button', colorClass, isActive && 'active')}
							onClick={() => setSelectedIndex(i)}
						>
							<span>{p.name}</span>
						</button>
					)
				})}
			</div>
		)
	}

	if (loading || error || !currentProgram) return null

	return (
		<>
			{showModulesSection && modulesSection && (
				<AAnnualProgramPreviewModules
					modulesSection={modulesSection}
					currentProgram={currentProgram}
					currentProgramConfig={currentProgramConfig}
					programModules={programModules}
					selectedModules={selectedModules}
					onModulesChange={setSelectedModules}
					link={link}
					renderSwitcher={renderSwitcher}
					showProgramsSwitch={modulesSection.showProgramsSwitch !== false}
					themeClass={getThemeClass()}
				/>
			)}

			<AAnnualProgramPreviewProgram
				id={id}
				className={className}
				style={style}
				link={link}
				programSection={programSectionResolved}
				programWithOrderUrl={programWithOrderUrl!}
				currentProgram={currentProgram}
				orderUrl={orderUrl}
				orderButtonLabelFormatted={orderButtonLabelFormatted}
				isFooterVisible={isFooterVisible}
				sectionRef={sectionRef}
				renderSwitcher={renderSwitcher}
				showProgramsSwitch={programSectionResolved.showProgramsSwitch !== false}
			/>
		</>
	)
}

MAnnualProgramPreview.displayName = 'MAnnualProgramPreview'
